/**
 * Supabase Auth `raw_user_meta_data` (via signUp / updateUser `data`).
 * Username is the handle; display_name / full_name / name are the public display label.
 */
import { generateFallbackDisplayName } from "./fallback-display-name";
import { isInternalEmail } from "./analytics/internal-user";

/** Sign-up & profile: handle validation */
export const PROFILE_USERNAME_PATTERN = /^[a-zA-Z0-9_]{3,30}$/;

/**
 * Metadata flag marking display_name/full_name/name as an auto-generated
 * fallback (email-only or nameless-OAuth signup) rather than a name the user
 * gave us. Stored as the string "true" to fit the string-only metadata shape;
 * cleared to "" the moment a real name is saved.
 */
export const FALLBACK_NAME_FLAG = "is_fallback_name";

export function normalizeDisplayName(raw: string): string {
  return raw.trim().replace(/\s+/g, " ");
}

export function isValidDisplayName(normalized: string): boolean {
  return normalized.length >= 2 && normalized.length <= 80;
}

/** e.g. `alex_kim` → `Alex Kim`, `john` → `John` */
export function humanizeUsernameForDisplay(username: string): string {
  const u = username.trim();
  if (!u) return "";
  return u
    .split(/_+/)
    .filter(Boolean)
    .map(
      (part) =>
        part.charAt(0).toUpperCase() + part.slice(1).toLowerCase(),
    )
    .join(" ");
}

export function buildUserMetadataFromUsername(
  username: string,
): Record<string, string> {
  const u = username.trim();
  const display = humanizeUsernameForDisplay(u) || u;
  return {
    username: u,
    display_name: display,
    full_name: display,
    name: display,
  };
}

/**
 * Synthesize a `username` handle from a first/last name pair, so legacy code
 * that reads `user_metadata.username` keeps getting a valid value even though
 * nothing asks the user for one. Strips diacritics and non-handle
 * characters; pads with a random suffix if the result would be shorter than
 * the 3-char minimum the profile editor enforces.
 */
function synthesizeUsername(firstName: string, lastName: string): string {
  const slug = `${firstName}${lastName}`
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9_]/g, "");
  if (slug.length >= 3) return slug.slice(0, 30);
  const suffix = Math.random().toString(36).slice(2, 6);
  return `${slug || "user"}_${suffix}`.slice(0, 30);
}

/**
 * First + last name → every derived key. Signup itself asks for neither (email
 * + password only, like Lovable); the name arrives later from Settings →
 * Account information, or from the OAuth backfill below.
 */
export function buildUserMetadataFromName(
  firstName: string,
  lastName: string,
): Record<string, string> {
  const first = firstName.trim();
  const last = lastName.trim();
  const display = normalizeDisplayName(`${first} ${last}`);
  return {
    first_name: first,
    last_name: last,
    username: synthesizeUsername(first, last),
    display_name: display,
    full_name: display,
    name: display,
    // A real name supersedes any earlier fallback — clear the marker.
    [FALLBACK_NAME_FLAG]: "",
  };
}

/**
 * Friendly fallback identity for a signup that supplied no name — email-only
 * (PANDA-1520 dropped the name fields) or an OAuth provider that shared none.
 * Seeded off a stable value (the email) so the SAME account always resolves to
 * the SAME name across sessions and surfaces.
 *
 * The two generated words are treated exactly like a real first/last name the
 * user typed: `first_name` / `last_name` are filled too (e.g. "Ivory" /
 * "Quokka"), so Settings → Account information shows the name and the display
 * name reads "Ivory Quokka" everywhere, just like a normal account. The only
 * difference is the fallback marker, which a later real name clears.
 */
export function buildFallbackNameMetadata(
  seed: string,
): Record<string, string> {
  const display = generateFallbackDisplayName(seed);
  const [firstWord, ...rest] = display.split(" ");
  return {
    ...buildUserMetadataFromName(firstWord, rest.join(" ")),
    [FALLBACK_NAME_FLAG]: "true",
  };
}

/**
 * OAuth signup (Google / Facebook / LinkedIn): the provider's payload lands
 * in `user_metadata` with the provider's own field names — Google uses
 * `given_name`/`family_name`, Facebook/LinkedIn typically only return `name`.
 * The settings UI (and a few email templates) read `first_name`/`last_name`,
 * which would otherwise stay empty for every Google signup.
 *
 * Returns the same shape as `buildUserMetadataFromName` so the rest of the
 * app sees no difference between an email-form signup and an OAuth one.
 * Callers should only pass this to `updateUser({ data })` when the existing
 * `first_name` is empty — Supabase merges, but we'd still overwrite a
 * user-edited value otherwise.
 *
 * Returns null when nothing usable could be derived (e.g. a future provider
 * that doesn't share name fields) so the caller can no-op cleanly.
 */
export function buildUserMetadataFromOAuth(
  metadata: Record<string, unknown> | null | undefined,
): Record<string, string> | null {
  const m = metadata ?? {};
  const s = (k: string) => (typeof m[k] === "string" ? (m[k] as string).trim() : "");

  // Google → given_name / family_name are the canonical split-name fields.
  let first = s("given_name");
  let last = s("family_name");

  // Facebook / LinkedIn → only `name` is reliably present; split on first
  // whitespace and treat the remainder as last name. One-word names go to
  // first_name only (matches what a user would do manually).
  if (!first && !last) {
    const fullName = s("full_name") || s("name");
    if (fullName) {
      const idx = fullName.indexOf(" ");
      if (idx === -1) {
        first = fullName;
      } else {
        first = fullName.slice(0, idx);
        last = fullName.slice(idx + 1).trim();
      }
    }
  }

  if (!first && !last) return null;

  const display = normalizeDisplayName(`${first} ${last}`);
  return {
    first_name: first,
    last_name: last,
    username: synthesizeUsername(first, last),
    display_name: display,
    full_name: display,
    name: display,
    // Provider gave us a real name — clear any earlier fallback marker.
    [FALLBACK_NAME_FLAG]: "",
  };
}

/** Profile editor: display name is user-controlled; keep full_name & name in sync. */
export function buildProfileUserMetadata(
  displayName: string,
  username: string,
): Record<string, string> {
  const u = username.trim();
  const d = normalizeDisplayName(displayName);
  return {
    username: u,
    display_name: d,
    full_name: d,
    name: d,
  };
}

/**
 * Resolve visible name from Supabase Auth `user_metadata` (display_name
 * preferred). When no name was ever stored (email-only account that hasn't
 * filled Settings yet), fall back to a friendly deterministic name seeded off
 * the email rather than the raw email local-part or a bare "User" — so the UI
 * never shows a placeholder, and the same account reads the same everywhere.
 */
export function getAuthDisplayName(
  metadata: Record<string, unknown> | undefined | null,
  email: string | undefined | null,
): string {
  const m = metadata ?? {};
  const s = (k: string) =>
    typeof m[k] === "string" ? (m[k] as string).trim() : "";
  return (
    s("display_name") ||
    s("full_name") ||
    s("name") ||
    s("username") ||
    (email ? generateFallbackDisplayName(email) : "") ||
    "User"
  );
}

/**
 * Resolve the best-available name fields for a PostHog person record from
 * Supabase Auth `user_metadata`. Returns only the keys that have a real,
 * non-blank value, so callers can spread the result into a PostHog `$set`
 * without ever overwriting an existing valid name with a blank/placeholder
 * (an omitted key leaves the existing person property untouched).
 *
 * Priority mirrors the product spec:
 *   first_name: first_name → given_name → first word of
 *               full_name → name → display_name → username
 *   full_name:  full_name → name → display_name → username
 *
 * Deliberately does NOT fall back to the email local-part or a literal
 * "User" the way getAuthDisplayName() does — those are display fallbacks,
 * not real names, and would pollute the person record. Returns {} when
 * nothing usable exists.
 */
export function buildPostHogNameProperties(
  metadata: Record<string, unknown> | null | undefined,
): { first_name?: string; full_name?: string } {
  const m = metadata ?? {};
  const s = (k: string) =>
    typeof m[k] === "string" ? (m[k] as string).trim() : "";
  const firstWord = (v: string) => v.split(/\s+/)[0] ?? "";

  const fullName =
    s("full_name") || s("name") || s("display_name") || s("username");

  const firstName =
    s("first_name") ||
    s("given_name") ||
    firstWord(s("full_name")) ||
    firstWord(s("name")) ||
    firstWord(s("display_name")) ||
    firstWord(s("username"));

  const out: { first_name?: string; full_name?: string } = {};
  if (firstName) out.first_name = firstName;
  if (fullName) out.full_name = fullName;
  return out;
}

/**
 * Person properties to attach to a PostHog identify / `$set`: email plus the
 * best-available name fields. Only includes keys with real values, so a login
 * with sparse metadata never clobbers a previously-set name. `user.id` stays
 * the distinct id at the call site, so email / first_name / full_name all
 * resolve to the same person.
 */
export function buildPostHogPersonProperties(user: {
  email?: string | null;
  user_metadata?: Record<string, unknown> | null;
}): Record<string, string | boolean> {
  const props: Record<string, string | boolean> = {};
  if (user.email) props.email = user.email;
  // Person-level marker so every client event this identity emits (not just the
  // upgrade funnel) can be filtered by internal-vs-real traffic in PostHog with
  // a single person condition, without threading the email through call sites.
  props.is_internal = isInternalEmail(user.email);
  const names = buildPostHogNameProperties(user.user_metadata);
  // No real name on the record → attach the same friendly fallback the emails
  // and UI use (seeded off email), so analytics people are never nameless. A
  // real name always wins, and the deterministic seed means no drift on later
  // logins. buildPostHogNameProperties itself stays fallback-free so it can be
  // reused where only genuine names belong.
  const fullName =
    names.full_name ||
    (user.email ? generateFallbackDisplayName(user.email) : "");
  const firstName =
    names.first_name || (fullName ? fullName.split(/\s+/)[0] : "");
  if (firstName) props.first_name = firstName;
  if (fullName) props.full_name = fullName;
  return props;
}

/** Profile image URL from `user_metadata` (Supabase / OAuth conventions). */
export function getAuthAvatarUrl(
  metadata: Record<string, unknown> | undefined | null,
): string | null {
  const m = metadata ?? {};
  const s = (k: string) => {
    const v = m[k];
    return typeof v === "string" && v.trim() ? v.trim() : "";
  };
  return s("avatar_url") || s("picture") || null;
}

export const USER_AVATARS_BUCKET = "user-avatars";

/** Object path inside bucket: `{userId}/avatar` */
export function userAvatarStoragePath(userId: string): string {
  return `${userId}/avatar`;
}
